import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const segmentedButtonVariants = cva('inline-flex rounded-full overflow-hidden', {
  variants: {
    size: {
      default: 'h-8 md:h-12',
      sm: 'h-8',
      lg: 'h-14',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const segmentVariants = cva(
  'relative flex items-center flex-grow justify-center gap-2 text-sm font-normal transition-all duration-200 cursor-pointer border border-border-default',
  {
    variants: {
      variant: {
        default: 'bg-white text-text-secondary',
        selected: 'bg-button-primary-selected-segment text-text-secondary',
        hovered: 'bg-background-hover text-text-secondary',
      },
      size: {
        default: 'px-4 py-3 text-sm h-8 md:h-12',
        sm: 'px-2 py-1 text-xs h-8',
        lg: 'px-5 py-4 text-base h-14',
      },
      position: {
        first: 'rounded-s-full border-r-0',
        middle: 'border-e-0',
        last: 'rounded-e-full',
        only: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      position: 'middle',
    },
  }
)

export interface SegmentedButtonOption {
  value: string
  label: string
  icon?: React.ReactNode
  flag?: React.ReactNode
}

export interface SegmentedButtonProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof segmentedButtonVariants> {
  options: SegmentedButtonOption[]
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  disabled?: boolean
  multiple?: boolean
}

const SegmentedButton = React.forwardRef<HTMLDivElement, SegmentedButtonProps>(
  ({ className, size, options, value, defaultValue, onChange, disabled = false, multiple = false, ...props }, ref) => {
    const [selectedValue, setSelectedValue] = React.useState(value || defaultValue || '')
    const [hoveredValue, setHoveredValue] = React.useState<string | null>(null)

    const handleClick = (optionValue: string) => {
      if (disabled) return

      const newValue = multiple ? (selectedValue === optionValue ? '' : optionValue) : optionValue

      setSelectedValue(newValue)
      onChange?.(newValue)
    }

    const handleMouseEnter = (optionValue: string) => {
      if (disabled) return
      setHoveredValue(optionValue)
    }

    const handleMouseLeave = () => {
      setHoveredValue(null)
    }

    const getPosition = (index: number) => {
      if (options.length === 1) return 'only'
      if (index === 0) return 'first'
      if (index === options.length - 1) return 'last'
      return 'middle'
    }

    return (
      <div ref={ref} className={cn(segmentedButtonVariants({ size, className }))} {...props}>
        {options.map((option, index) => {
          const isSelected = selectedValue === option.value
          const isHovered = hoveredValue === option.value

          return (
            <button
              key={option.value}
              type='button'
              className={cn(
                segmentVariants({
                  variant: isSelected ? 'selected' : isHovered ? 'hovered' : 'default',
                  size,
                  position: getPosition(index),
                }),
                disabled && 'cursor-not-allowed opacity-50'
              )}
              onClick={() => handleClick(option.value)}
              onMouseEnter={() => handleMouseEnter(option.value)}
              onMouseLeave={handleMouseLeave}
              disabled={disabled}
            >
              {option.flag && <div className='flex-shrink-0'>{option.flag}</div>}
              {option.icon && <div className='flex-shrink-0'>{option.icon}</div>}
              <span className='whitespace-nowrap'>{option.label}</span>
            </button>
          )
        })}
      </div>
    )
  }
)

SegmentedButton.displayName = 'SegmentedButton'

export { SegmentedButton, segmentedButtonVariants }
